import { EntityType } from '@crew/enums/domain'
import { RelatedSuggestion } from '@crew/models/domain'
import { useTranslation } from '@crew/modules/i18n'
import { useSubscribeEventRelatedInfo } from '@crew/providers/websocket'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewAiAssistantAvatar } from 'components/elements/crewAiAssistantAvatar/crewAiAssistantAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { Modal } from 'components/layouts/modal/modal'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { FC, memo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useEventDetailRelatedInfoListContext } from '../../eventDetailRelatedInfoList'
import { SuggestionTable } from '../eventDetailRelatedInfoListPanel/components/suggestionTable/suggestionTable'
import { useEventDetailRelatedInfoListToolbar } from '../eventDetailRelatedInfoListToolbar/useEventDetailRelatedInfoListToolbar'

export const EventDetailRelatedInfoSuggestionDialog: FC = memo(() => {
  const { t } = useTranslation()
  const { eventId } = useParams()
  const [showApiErrors] = useShowApiErrors()
  const toast = useToast()

  if (!eventId) {
    throw new Error('eventId is undefined')
  }

  const {
    displayState,
    setDisplayState,
    setRelatedSuggestions,
    setSelectedSuggestions,
    selectedSuggestions,
  } = useEventDetailRelatedInfoListContext()

  const { insertEntityReferences, isLoadingInsertEntityReferencesMutation } =
    useEventDetailRelatedInfoListToolbar()

  const showSearching = displayState === 'searching'
  const showSuggestionTable = displayState === 'suggestion'
  const isOpenDialog = displayState !== 'relatedInfo'

  // ダイアログを閉じるときに関連提案状態を初期化する
  const handleCloseDialog = useCallback(() => {
    setSelectedSuggestions([])
    setRelatedSuggestions([])
    setDisplayState('relatedInfo')
  }, [setDisplayState, setRelatedSuggestions, setSelectedSuggestions])

  // 関連情報提案を登録する
  const handleAddRelatedInfoButtonClick = useCallback(async () => {
    try {
      await insertEntityReferences(selectedSuggestions)
      toast.success(t('message.entityReference.entityReferenceCreated'))
      handleCloseDialog()
    } catch (err) {
      showApiErrors(err)
    }
  }, [
    handleCloseDialog,
    insertEntityReferences,
    selectedSuggestions,
    showApiErrors,
    t,
    toast,
  ])

  // 会議関連情報提案作成完了したときのコールバック関数
  const handleEventRelatedSuggestionCreated = useCallback(
    (relatedSuggestion: RelatedSuggestion) => {
      if (displayState === 'relatedInfo') {
        return
      }

      // 受信したsuggestionが現在表示している会議のものか確認
      if (
        relatedSuggestion.sourceEntityType !== EntityType.Event ||
        relatedSuggestion.sourceEntityRecordId !== eventId
      ) {
        return
      }

      const suggestionItems = relatedSuggestion.relatedItems.map(
        ({ entityType, entityRecordId, title, updatedBy, updatedAt }) => ({
          id: `${entityType}|${entityRecordId}`,
          entityType,
          entityRecordId,
          title,
          updatedBy,
          updatedAt,
        })
      )
      setRelatedSuggestions(suggestionItems)
      setDisplayState('suggestion')
    },
    [displayState, eventId, setDisplayState, setRelatedSuggestions]
  )

  // 会議関連情報提案作成失敗したときのコールバック関数
  const handleEventRelatedSuggestionFailed = useCallback(
    (relatedSuggestion: RelatedSuggestion) => {
      if (displayState === 'relatedInfo') {
        return
      }

      // 提案失敗通知が現在表示している会議のものか確認
      if (
        relatedSuggestion.sourceEntityType !== EntityType.Event ||
        relatedSuggestion.sourceEntityRecordId !== eventId
      ) {
        return
      }

      setRelatedSuggestions([])
      setSelectedSuggestions([])
      setDisplayState('relatedInfo')
      toast.error(t('message.entityReference.relatedSuggestionCreateFailed'))
    },
    [
      displayState,
      eventId,
      setDisplayState,
      setRelatedSuggestions,
      setSelectedSuggestions,
      t,
      toast,
    ]
  )

  // 会議の関連情報の提案結果をsubscribeする
  useSubscribeEventRelatedInfo(
    handleEventRelatedSuggestionCreated,
    handleEventRelatedSuggestionFailed
  )

  return (
    <Modal
      id="event-detail-related-info-suggestion-dialog"
      title={t('label.addRelatedInfoDialogTitle')}
      isOpen={isOpenDialog}
      onClose={handleCloseDialog}
      closeDisabled={showSearching}
    >
      <div className="flex flex-col gap-y-3 h-full min-h-0 max-h-[640px]">
        {showSearching && (
          <div className="flex flex-1 flex-row items-center justify-center gap-3">
            <CrewAiAssistantAvatar
              size={CrewAvatarSize.base}
              displayName={''} // ホバーしても何も表示しないため空文字列を設定
              cacheValue={''} // キャッシュを無効化するため空文字列を設定
            />
            {t('label.aiIsSearchingRelatedInfo')}
          </div>
        )}

        {showSuggestionTable && (
          <>
            <div className="min-h-0 overflow-y-auto">
              <SuggestionTable />
            </div>
            <div className="ml-auto flex flex-row items-center gap-x-2.5">
              <CrewButton
                id="event-detail-related-info-list-dialog-add-related-info-button"
                type="primary"
                text={t('action.addRelatedInformation')}
                onClick={handleAddRelatedInfoButtonClick}
                disabled={
                  selectedSuggestions.length === 0 ||
                  isLoadingInsertEntityReferencesMutation
                }
              />
              <CrewButton
                id="event-detail-related-info-list-dialog-cancel-adding-related-info-button"
                type="normal"
                stylingMode="outlined"
                text={t('action.cancel')}
                onClick={handleCloseDialog}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  )
})
